import { useEffect, useRef } from "react";

export default function AtmosphereHero({ className = "", intensity = 1 }) {
  const cv = useRef(null);

  useEffect(() => {
    const canvas = cv.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const REDUCED =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const DPR = Math.min(1.5, window.devicePixelRatio || 1);
    let W = 0;
    let H = 0;
    let grain = null;
    let visible = true;
    let raf = 0;
    let last = 0;
    let t = 26;

    const makeGrain = () => {
      const g = document.createElement("canvas");
      g.width = 96;
      g.height = 96;
      const gc = g.getContext("2d");
      const img = gc.createImageData(96, 96);
      for (let i = 0; i < img.data.length; i += 4) {
        const v = 128 + (Math.random() - 0.5) * 3;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 10;
      }
      gc.putImageData(img, 0, 0);
      return g;
    };

    const size = () => {
      W = canvas.clientWidth || 1;
      H = canvas.clientHeight || 1;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };

    size();
    grain = makeGrain();

    const onResize = () => size();
    window.addEventListener("resize", onResize, { passive: true });

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(size) : null;
    if (ro) ro.observe(canvas);

    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver((e) => {
            visible = e[0].isIntersecting;
          })
        : null;
    if (io) io.observe(canvas);

    const bodies = [
      { ax: 0.52, ay: 0.4, rx: 0.34, ry: 0.24, px: 0.061, py: 0.043, ph: 0.0, r: 0.3, a: 0.408 },
      { ax: 0.66, ay: 0.55, rx: 0.3, ry: 0.28, px: 0.047, py: 0.036, ph: 1.7, r: 0.34, a: 0.336 },
      { ax: 0.78, ay: 0.34, rx: 0.22, ry: 0.2, px: 0.033, py: 0.052, ph: 3.1, r: 0.26, a: 0.278 },
      { ax: 0.44, ay: 0.66, rx: 0.26, ry: 0.18, px: 0.025, py: 0.029, ph: 4.4, r: 0.31, a: 0.221 },
      { ax: 0.88, ay: 0.62, rx: 0.18, ry: 0.22, px: 0.019, py: 0.041, ph: 5.6, r: 0.22, a: 0.182 },
    ];

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const big = Math.max(W, H);
      for (let k = 0; k < bodies.length; k += 1) {
        const b = bodies[k];
        const cx = W * (b.ax + b.rx * Math.sin(t * b.px + b.ph));
        const cy = H * (b.ay + b.ry * Math.cos(t * b.py + b.ph * 0.7));
        const r = big * b.r * (0.9 + 0.1 * Math.sin(t * 0.021 + b.ph));
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        const a = b.a * intensity;
        g.addColorStop(0, "rgba(0,112,126," + a.toFixed(4) + ")");
        g.addColorStop(0.55, "rgba(0,84,104," + (a * 0.42).toFixed(4) + ")");
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (grain) {
        const p = ctx.createPattern(grain, "repeat");
        if (p) {
          ctx.fillStyle = p;
          ctx.fillRect(0, 0, W, H);
        }
      }
    };

    if (REDUCED) {
      draw();
    } else {
      const loop = (now) => {
        const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.0167;
        last = now;
        if (visible) {
          t += dt;
          draw();
        }
        raf = window.requestAnimationFrame(loop);
      };
      raf = window.requestAnimationFrame(loop);
    }

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      if (io) io.disconnect();
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [intensity]);

  return (
    <div className={"absolute inset-0 " + className} aria-hidden="true">
      <canvas ref={cv} className="h-full w-full block" />
    </div>
  );
}
